import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { User, Mail, Lock, Palette, Users, Save, Eye, EyeOff, Key, Copy, Check, RefreshCw, DollarSign, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { invalidateAccountBalances, beginSync, endSync } from '../hooks/useAccountBalances';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VALID_TABS = ['personal', 'team', 'app', 'balances', 'api'];

function Settings() {
  const { user, updateUser } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();
  const initialTab = (() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('tab');
    return VALID_TABS.includes(requested) ? requested : 'personal';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync tab when the URL query changes (e.g. user clicks SystemHealthPill CTA)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('tab');
    if (requested && VALID_TABS.includes(requested)) {
      setActiveTab((prev) => (prev === requested ? prev : requested));
    }
  }, [location.search]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailSummaryStatus, setEmailSummaryStatus] = useState('');
  
  // API Key State
  const [apiKeyStatus, setApiKeyStatus] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  
  // Actual Balances State
  const [actualBalances, setActualBalances] = useState({
    personal: 0,
    business: 0,
    taxes: 0,
    savings: 0,
    buffer: 0,
    custom_allocations: {},
    // Path A (Feb 2026) — optional free-text label per bucket describing
    // which real-world account funds it (e.g. "Chase HYSA ****4321").
    // Purely a display hint; no math impact.
    sources: {},
    custom_sources: {}
  });
  const [balanceComparison, setBalanceComparison] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesSaving, setBalancesSaving] = useState(false);
  
  // Personal Info State
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Team Settings State
  const [teamSettings, setTeamSettings] = useState({
    teamName: '',
    teamLogo: ''
  });

  // App Settings State
  const [appSettings, setAppSettings] = useState({
    darkMode: false,
    notifications: true,
    autoSave: true
  });

  useEffect(() => {
    if (user) {
      setPersonalInfo({
        name: user.name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setTeamSettings({
        teamName: user.team_name || '',
        teamLogo: user.team_logo || ''
      });
    }
    
    // Load app settings from localStorage and sync with dark mode context
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setAppSettings(settings);
    }
    
    // Sync with current dark mode state
    setAppSettings(prev => ({ ...prev, darkMode }));
    
    // Check API key status
    checkApiKeyStatus();
  }, [user, darkMode]);

  const checkApiKeyStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/api-key-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeyStatus(response.data);
    } catch (error) {
      console.error('Error checking API key status:', error);
    }
  };

  const generateApiKey = async () => {
    setApiKeyLoading(true);
    setNewApiKey('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/generate-api-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewApiKey(response.data.api_key);
      setApiKeyStatus({ has_active_key: true, created_at: new Date().toISOString() });
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to generate API key');
    }
    setApiKeyLoading(false);
  };

  const revokeApiKey = async () => {
    if (!window.confirm('Are you sure you want to revoke your API key? Any external integrations using this key will stop working.')) {
      return;
    }
    setApiKeyLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/settings/revoke-api-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeyStatus({ has_active_key: false });
      setNewApiKey('');
      setMessage('API key revoked successfully');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to revoke API key');
    }
    setApiKeyLoading(false);
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(newApiKey);
    setApiKeyCopied(true);
    setTimeout(() => setApiKeyCopied(false), 2000);
  };

  // Load actual balances
  const loadActualBalances = async () => {
    setBalancesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [balancesRes, comparisonRes] = await Promise.all([
        axios.get(`${API}/settings/actual-balances`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/settings/balance-comparison`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setActualBalances(balancesRes.data);
      setBalanceComparison(comparisonRes.data);
    } catch (error) {
      console.error('Error loading balances:', error);
    }
    setBalancesLoading(false);
  };

  // Save actual balances
  const saveActualBalances = async () => {
    setBalancesSaving(true);
    setMessage('');
    beginSync('Saving balances');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/actual-balances`, actualBalances, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Reload comparison after saving
      const comparisonRes = await axios.get(`${API}/settings/balance-comparison`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalanceComparison(comparisonRes.data);
      setMessage('Balances saved successfully!');
      // Actual balances feed the Rebalance system + drive Account Health meters —
      // invalidate so every view refreshes immediately.
      invalidateAccountBalances();
      endSync({ success: true });
    } catch (error) {
      endSync({ success: false });
      setMessage(error.response?.data?.detail || 'Failed to save balances');
    }
    setBalancesSaving(false);
  };

  // Load balances when tab changes to balances
  useEffect(() => {
    if (activeTab === 'balances') {
      loadActualBalances();
    }
  }, [activeTab]);

  const handlePersonalInfoSave = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: personalInfo.name,
        email: personalInfo.email
      };

      // Add password change if provided
      if (personalInfo.newPassword) {
        if (personalInfo.newPassword !== personalInfo.confirmPassword) {
          setMessage('New passwords do not match');
          setLoading(false);
          return;
        }
        if (!personalInfo.currentPassword) {
          setMessage('Current password is required to set new password');
          setLoading(false);
          return;
        }
        updateData.currentPassword = personalInfo.currentPassword;
        updateData.newPassword = personalInfo.newPassword;
      }

      const response = await axios.put(`${API}/user/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser(response.data);
      setMessage('Profile updated successfully!');
      
      // Clear password fields
      setPersonalInfo(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update profile');
    }
    
    setLoading(false);
  };

  const handleTeamNameSave = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/user/team-name`, 
        { team_name: teamSettings.teamName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      updateUser(response.data);
      setMessage('Team name updated successfully!');
      
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update team name');
    }
    
    setLoading(false);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('Image size must be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Image = e.target.result;
      
      setLoading(true);
      setMessage('');
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `${API}/user/team-logo`,
          { team_logo: base64Image },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updateUser(response.data);
        setTeamSettings({...teamSettings, teamLogo: base64Image});
        setMessage('Team logo uploaded successfully!');
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Failed to upload team logo');
      }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/user/team-logo`,
        { team_logo: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(response.data);
      setTeamSettings({...teamSettings, teamLogo: ''});
      setMessage('Team logo removed successfully!');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to remove team logo');
    }
    setLoading(false);
  };

  const handleAppSettingsSave = () => {
    localStorage.setItem('appSettings', JSON.stringify(appSettings));
    setMessage('Settings saved successfully!');
    
    // Apply dark mode through the context
    toggleDarkMode(appSettings.darkMode);
  };
  const sendEmailSummary = async (period) => {
    setEmailSummaryStatus('');
    try {
      const token = localStorage.getItem('token');
      const endpoint = period === 'weekly' ? 'send-weekly-summary' : 'send-monthly-summary';
      const response = await axios.post(`${API}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSummaryStatus(`📬 ${period.charAt(0).toUpperCase() + period.slice(1)} summary sent successfully! Check your email.`);
    } catch (error) {
      setEmailSummaryStatus(`❌ ${error.response?.data?.detail || `Failed to send ${period} summary`}`);
    }
  };

  const sendTeamSummary = async (period) => {
    setEmailSummaryStatus('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/send-team-summary/${period}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSummaryStatus(`📬 Team ${period} summary sent successfully! Check your email.`);
    } catch (error) {
      setEmailSummaryStatus(`❌ ${error.response?.data?.detail || `Failed to send team ${period} summary`}`);
    }
  };

  // Dead code removed: importTeamData, checkTeamData, deleteChadAccount, cleanupDuplicates, standardizeCategories
  // These functions were part of a removed admin feature and are no longer used

  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteStatus, setDeleteStatus] = useState('');
  const [recalcStatus, setRecalcStatus] = useState('');

  const deleteMember = async () => {
    if (!deleteEmail.trim()) {
      setDeleteStatus('❌ Please enter an email address');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${deleteEmail}? This action cannot be undone.`)) {
      return;
    }

    setDeleteStatus('🗑️ Deleting member...');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/team/delete-member/${encodeURIComponent(deleteEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeleteStatus(`✅ ${response.data.message}`);
      setDeleteEmail(''); // Clear the input
      
      // Show reassignment info if any
      if (response.data.orphaned_members_reassigned > 0) {
        setDeleteStatus(prev => prev + `\n\n📌 ${response.data.orphaned_members_reassigned} orphaned member(s) have been reassigned to you.`);
      }
    } catch (error) {
      setDeleteStatus(`❌ ${error.response?.data?.detail || 'Failed to delete member'}`);
    }
  };

  const isLeader = ['satellite', 'fsl', 'fta'].includes(user?.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:text-white mb-2">Settings</h1>
        <p className="text-gray-600 dark:text-gray-300 dark:text-gray-300">Manage your account and application preferences</p>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap gap-2 px-4 py-2 sm:px-6 sm:gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('personal')}
              className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'personal'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <User className="w-4 h-4 inline mr-1" />
              Personal Info
            </button>
            
            {isLeader && (
              <button
                onClick={() => setActiveTab('team')}
                className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'team'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" />
                Team
              </button>
            )}
            
            <button
              onClick={() => setActiveTab('app')}
              className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'app'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Palette className="w-4 h-4 inline mr-1" />
              Preferences
            </button>
            
            <button
              onClick={() => setActiveTab('balances')}
              className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'balances'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <DollarSign className="w-4 h-4 inline mr-1" />
              Balances
            </button>
            
            <button
              onClick={() => setActiveTab('api')}
              className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'api'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Key className="w-4 h-4 inline mr-1" />
              API
            </button>
          </nav>
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-6 mt-4 p-3 rounded-lg ${
            message.includes('successfully') || message.includes('saved')
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
          }`}>
            {message}
          </div>
        )}

        {/* Tab Content */}
        <div className="p-6">
          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 font-semibold mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={personalInfo.name}
                    onChange={(e) => setPersonalInfo({...personalInfo, name: e.target.value})}
                    className="w-full bg-white border border-gray-300 dark:border-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 font-semibold mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={personalInfo.email}
                    onChange={(e) => setPersonalInfo({...personalInfo, email: e.target.value})}
                    className="w-full bg-white border border-gray-300 dark:border-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Password Change Section */}
              <div className="border-t dark:border-gray-700 pt-6">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white dark:text-white mb-4">Change Password</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={personalInfo.currentPassword}
                        onChange={(e) => setPersonalInfo({...personalInfo, currentPassword: e.target.value})}
                        className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:text-gray-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      New Password
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={personalInfo.newPassword}
                      onChange={(e) => setPersonalInfo({...personalInfo, newPassword: e.target.value})}
                      className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="New password"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={personalInfo.confirmPassword}
                      onChange={(e) => setPersonalInfo({...personalInfo, confirmPassword: e.target.value})}
                      className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handlePersonalInfoSave}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </button>
            </div>
          )}

          {/* Team Settings Tab */}
          {activeTab === 'team' && isLeader && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Team Settings</h3>
              
              {/* Team Logo Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team Logo
                </label>
                <div className="flex items-center space-x-4">
                  {teamSettings.teamLogo ? (
                    <div className="relative">
                      <img 
                        src={teamSettings.teamLogo} 
                        alt="Team Logo" 
                        className="w-24 h-24 object-contain rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      />
                      <button
                        onClick={handleRemoveLogo}
                        disabled={loading}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:opacity-50"
                        title="Remove logo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-700">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={loading}
                      className="hidden"
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {teamSettings.teamLogo ? 'Change Logo' : 'Upload Logo'}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Your team logo will appear on the "My Team" page above your team name
                </p>
              </div>

              {/* Team Name Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Team Name
                </label>
                <input
                  type="text"
                  value={teamSettings.teamName}
                  onChange={(e) => setTeamSettings({...teamSettings, teamName: e.target.value})}
                  className="w-full max-w-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`${user?.role?.toUpperCase()} Team`}
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  This name will appear on your "My Team" page header
                </p>
              </div>

              <button
                onClick={handleTeamNameSave}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Team Name
              </button>
            </div>
          )}

          {/* App Preferences Tab */}
          {activeTab === 'app' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">Application Preferences</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white dark:text-white">Dark Mode</h4>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold dark:text-gray-400">Enable dark theme for better night viewing</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={appSettings.darkMode}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setAppSettings({...appSettings, darkMode: newValue});
                        toggleDarkMode(newValue); // Apply immediately
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white dark:text-white">Email Notifications</h4>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold dark:text-gray-400">Receive email updates about your account</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={appSettings.notifications}
                      onChange={(e) => setAppSettings({...appSettings, notifications: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white dark:text-white">Auto-Save</h4>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold dark:text-gray-400">Automatically save form data as you type</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={appSettings.autoSave}
                      onChange={(e) => setAppSettings({...appSettings, autoSave: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
              {/* Email Summary Actions */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mt-6">
                <h4 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-3">📧 Email Financial Summaries</h4>
                <p className="text-sm text-blue-700 dark:text-blue-200 mb-4">Get comprehensive financial reports delivered to your email</p>
                
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => sendEmailSummary('weekly')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    📊 Send Weekly Summary
                  </button>
                  
                  <button
                    onClick={() => sendEmailSummary('monthly')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    📈 Send Monthly Summary
                  </button>
                  
                  {user?.role && ['satellite', 'fsl', 'fta'].includes(user.role.toLowerCase()) && (
                    <>
                      <button
                        onClick={() => sendTeamSummary('weekly')}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        👥 Send Team Weekly
                      </button>
                      
                      <button
                        onClick={() => sendTeamSummary('monthly')}
                        className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        👥 Send Team Monthly
                      </button>
                    </>
                  )}
                </div>
                
                {emailSummaryStatus && (
                  <div className={`mt-3 p-2 rounded ${
                    emailSummaryStatus.includes('successfully') 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                  }`}>
                    {emailSummaryStatus}
                  </div>
                )}
              </div>

              {/* Data Repair - Fix Missing Allocations */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 mt-6">
                <h4 className="text-lg font-medium text-amber-900 dark:text-amber-100 mb-3">🔧 Data Repair</h4>
                <p className="text-sm text-amber-700 dark:text-amber-200 mb-4">
                  Fix data inconsistencies in your account
                </p>
                
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={async () => {
                      setRecalcStatus('🔄 Recalculating allocations...');
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.post(`${API}/admin/recalculate-allocations`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (response.data.entries_updated > 0) {
                          setRecalcStatus(`✅ Fixed ${response.data.entries_updated} entries! Refresh the 52-Week Tracker to see updated values.`);
                        } else {
                          setRecalcStatus('✅ All entries already have proper allocations - nothing to fix!');
                        }
                      } catch (error) {
                        setRecalcStatus(`❌ ${error.response?.data?.detail || 'Failed to recalculate'}`);
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    🔄 Recalculate Allocations
                  </button>
                  
                  <button
                    onClick={async () => {
                      setRecalcStatus('🔄 Standardizing expense categories...');
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.post(`${API}/admin/standardize-categories`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (response.data.updated_count > 0) {
                          const categories = Object.entries(response.data.categories_standardized || {})
                            .map(([from, to]) => `${from} → ${to}`)
                            .join(', ');
                          setRecalcStatus(`✅ Standardized ${response.data.updated_count} expense categories: ${categories}`);
                        } else {
                          setRecalcStatus('✅ All expense categories are already standardized!');
                        }
                      } catch (error) {
                        setRecalcStatus(`❌ ${error.response?.data?.detail || 'Failed to standardize categories'}`);
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    🏷️ Standardize Expense Categories
                  </button>
                </div>
                
                {recalcStatus && (
                  <div className={`mt-3 p-2 rounded text-sm ${
                    recalcStatus.includes('✅')
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                      : recalcStatus.includes('❌')
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                  }`}>
                    {recalcStatus}
                  </div>
                )}
              </div>

              {/* Delete Member - For Leaders Only */}
              {isLeader && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mt-6">
                  <h4 className="text-lg font-medium text-red-900 dark:text-red-100 mb-3">🗑️ Delete Team Member</h4>
                  <p className="text-sm text-red-700 dark:text-red-200 mb-4">Remove a member from your team hierarchy</p>
                  
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={deleteEmail}
                        onChange={(e) => setDeleteEmail(e.target.value)}
                        placeholder="Enter member's email address"
                        className="flex-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                      <button
                        onClick={deleteMember}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        Delete Member
                      </button>
                    </div>
                    
                    {deleteStatus && (
                      <div className={`p-3 rounded ${
                        deleteStatus.includes('✅')
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                      }`}>
                        <pre className="whitespace-pre-wrap text-xs font-mono">{deleteStatus}</pre>
                      </div>
                    )}
                    
                    <p className="text-xs text-red-600 dark:text-red-300">
                      ⚠️ Warning: This action cannot be undone. Members under the deleted user will be reassigned to you.
                    </p>
                  </div>
                </div>
              )}

              {/* Admin section removed */}

              <button
                onClick={handleAppSettingsSave}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Preferences
              </button>
            </div>
          )}

          {/* My Balances Tab */}
          {activeTab === 'balances' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">My Account Balances</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Enter your current actual balances to compare with expected allocations.
                </p>
              </div>

              {balancesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Loading balances...</span>
                </div>
              ) : (
                <>
                  {/* Warnings */}
                  {balanceComparison?.warnings?.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Balance Discrepancies</h4>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300/90 mt-1">
                            These values compare your actual balances to where they should be based on your income and allocation plan.
                          </p>
                          <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                            {balanceComparison.warnings.map((warning, idx) => (
                              <li key={warning}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Path A (Feb 2026) — Source-account duplicate-split warning.
                      Non-blocking; alerts the user when the SAME free-text
                      source label is attached to 2+ buckets that BOTH hold
                      non-zero balances (classic HYSA-holds-tax-and-savings
                      pattern). No math changes, purely a UX reminder. */}
                  {(() => {
                    const src = actualBalances.sources || {};
                    const csrc = actualBalances.custom_sources || {};
                    const bucketVals = {
                      personal: actualBalances.personal || 0,
                      business: actualBalances.business || 0,
                      taxes: actualBalances.taxes || 0,
                      savings: actualBalances.savings || 0,
                      buffer: actualBalances.buffer || 0,
                    };
                    const customVals = actualBalances.custom_allocations || {};
                    // Group non-empty bucket labels by normalized value
                    const grouped = {};
                    Object.entries(src).forEach(([bucket, label]) => {
                      const key = (label || '').trim().toLowerCase();
                      if (!key) return;
                      if ((bucketVals[bucket] || 0) === 0) return;
                      grouped[key] = grouped[key] || { display: label.trim(), buckets: [] };
                      grouped[key].buckets.push(bucket.charAt(0).toUpperCase() + bucket.slice(1));
                    });
                    Object.entries(csrc).forEach(([name, label]) => {
                      const key = (label || '').trim().toLowerCase();
                      if (!key) return;
                      if ((customVals[name] || 0) === 0) return;
                      grouped[key] = grouped[key] || { display: label.trim(), buckets: [] };
                      grouped[key].buckets.push(name);
                    });
                    const dupes = Object.values(grouped).filter(g => g.buckets.length > 1);
                    if (dupes.length === 0) return null;
                    return (
                      <div
                        className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4"
                        role="status"
                        data-testid="balance-source-split-warning"
                      >
                        <div className="flex items-start">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-amber-900 dark:text-amber-200">Same source account across multiple buckets</h4>
                            <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                              If one physical account holds money for two buckets, split the amounts manually — don't enter the same balance in both. Otherwise you'll double-count.
                            </p>
                            <ul className="mt-2 text-xs text-amber-800 dark:text-amber-300 space-y-1">
                              {dupes.map(d => (
                                <li key={d.display}>
                                  • <strong>{d.display}</strong> is currently the source for: <em>{d.buckets.join(', ')}</em> — confirm each amount is a distinct pod.
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Balance Input Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Personal Account */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                      <label className="block text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        Personal Account
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={actualBalances.personal || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            personal: parseFloat(e.target.value) || 0
                          }))}
                          className="w-full pl-8 pr-4 py-2 border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="0.00"
                          data-testid="balance-personal"
                        />
                      </div>
                      {/* Path A — optional source label */}
                      <div className="mt-2">
                        <label className="block text-[11px] font-medium text-blue-700 dark:text-blue-300 mb-1">
                          Source account (optional)
                        </label>
                        <input
                          type="text"
                          value={actualBalances.sources?.personal || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            sources: { ...(prev.sources || {}), personal: e.target.value }
                          }))}
                          className="w-full px-3 py-1.5 text-xs border border-blue-200 dark:border-blue-800 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                          placeholder="e.g. Chase Checking ****1234"
                          data-testid="balance-source-personal"
                          maxLength={80}
                        />
                      </div>
                      {balanceComparison?.comparison?.personal && (
                        <div className="mt-2 text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Expected: </span>
                          <span className={balanceComparison.comparison.personal.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                            ${balanceComparison.comparison.personal.expected.toLocaleString()}
                          </span>
                          {balanceComparison.comparison.personal.difference !== 0 && (
                            <span className={`ml-2 ${balanceComparison.comparison.personal.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              ${Math.abs(balanceComparison.comparison.personal.difference).toLocaleString()} {balanceComparison.comparison.personal.difference > 0 ? 'above' : 'below'} target allocation
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Business Account */}
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                      <label className="block text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
                        Business Account
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={actualBalances.business || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            business: parseFloat(e.target.value) || 0
                          }))}
                          className="w-full pl-8 pr-4 py-2 border border-purple-300 dark:border-purple-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="0.00"
                          data-testid="balance-business"
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-[11px] font-medium text-purple-700 dark:text-purple-300 mb-1">
                          Source account (optional)
                        </label>
                        <input
                          type="text"
                          value={actualBalances.sources?.business || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            sources: { ...(prev.sources || {}), business: e.target.value }
                          }))}
                          className="w-full px-3 py-1.5 text-xs border border-purple-200 dark:border-purple-800 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                          placeholder="e.g. Business Checking ****5678"
                          data-testid="balance-source-business"
                          maxLength={80}
                        />
                      </div>
                      {balanceComparison?.comparison?.business && (
                        <div className="mt-2 text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Expected: </span>
                          <span className={balanceComparison.comparison.business.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                            ${balanceComparison.comparison.business.expected.toLocaleString()}
                          </span>
                          {balanceComparison.comparison.business.difference !== 0 && (
                            <span className={`ml-2 ${balanceComparison.comparison.business.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              ${Math.abs(balanceComparison.comparison.business.difference).toLocaleString()} {balanceComparison.comparison.business.difference > 0 ? 'above' : 'below'} target allocation
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Taxes Account */}
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                      <label className="block text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                        Taxes Account
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={actualBalances.taxes || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            taxes: parseFloat(e.target.value) || 0
                          }))}
                          className="w-full pl-8 pr-4 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="0.00"
                          data-testid="balance-taxes"
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-[11px] font-medium text-red-700 dark:text-red-300 mb-1">
                          Source account (optional)
                        </label>
                        <input
                          type="text"
                          value={actualBalances.sources?.taxes || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            sources: { ...(prev.sources || {}), taxes: e.target.value }
                          }))}
                          className="w-full px-3 py-1.5 text-xs border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                          placeholder="e.g. HYSA ****4321 (Tax portion)"
                          data-testid="balance-source-taxes"
                          maxLength={80}
                        />
                      </div>
                      <p className="mt-2 text-[11px] text-red-800/80 dark:text-red-200/80 leading-snug">
                        If one physical account holds both tax reserve and savings, manually split the amounts across Taxes and Savings. Do not enter the same balance in both.
                      </p>
                      {balanceComparison?.comparison?.taxes && (
                        <div className="mt-2 text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Expected: </span>
                          <span className={balanceComparison.comparison.taxes.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                            ${balanceComparison.comparison.taxes.expected.toLocaleString()}
                          </span>
                          {balanceComparison.comparison.taxes.difference !== 0 && (
                            <span className={`ml-2 ${balanceComparison.comparison.taxes.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              ${Math.abs(balanceComparison.comparison.taxes.difference).toLocaleString()} {balanceComparison.comparison.taxes.difference > 0 ? 'above' : 'below'} target allocation
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Savings Account */}
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <label className="block text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                        Savings Account
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={actualBalances.savings || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            savings: parseFloat(e.target.value) || 0
                          }))}
                          className="w-full pl-8 pr-4 py-2 border border-green-300 dark:border-green-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="0.00"
                          data-testid="balance-savings"
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-[11px] font-medium text-green-700 dark:text-green-300 mb-1">
                          Source account (optional)
                        </label>
                        <input
                          type="text"
                          value={actualBalances.sources?.savings || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            sources: { ...(prev.sources || {}), savings: e.target.value }
                          }))}
                          className="w-full px-3 py-1.5 text-xs border border-green-200 dark:border-green-800 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                          placeholder="e.g. HYSA ****4321 (Savings portion)"
                          data-testid="balance-source-savings"
                          maxLength={80}
                        />
                      </div>
                      <p className="mt-2 text-[11px] text-green-800/80 dark:text-green-200/80 leading-snug">
                        Savings should only include dedicated savings/emergency/future-goal money. If your HYSA also holds tax reserve, split the totals across Taxes and Savings — don't double-count.
                      </p>
                      {balanceComparison?.comparison?.savings && (
                        <div className="mt-2 text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Expected: </span>
                          <span className={balanceComparison.comparison.savings.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                            ${balanceComparison.comparison.savings.expected.toLocaleString()}
                          </span>
                          {balanceComparison.comparison.savings.difference !== 0 && (
                            <span className={`ml-2 ${balanceComparison.comparison.savings.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              ${Math.abs(balanceComparison.comparison.savings.difference).toLocaleString()} {balanceComparison.comparison.savings.difference > 0 ? 'above' : 'below'} target allocation
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Buffer Account — first-class bucket (Feb 2026).
                        Holds unallocated capital retained outside the core
                        plan buckets. */}
                    <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4 border border-cyan-200 dark:border-cyan-800">
                      <label className="block text-sm font-medium text-cyan-800 dark:text-cyan-200 mb-1">
                        Buffer Account
                      </label>
                      <p className="text-[11px] text-cyan-700 dark:text-cyan-300 mb-2">
                        Unallocated capital retained outside plan buckets.
                      </p>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={actualBalances.buffer || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            buffer: parseFloat(e.target.value) || 0
                          }))}
                          className="w-full pl-8 pr-4 py-2 border border-cyan-300 dark:border-cyan-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="0.00"
                          data-testid="balance-buffer"
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-[11px] font-medium text-cyan-700 dark:text-cyan-300 mb-1">
                          Source account (optional)
                        </label>
                        <input
                          type="text"
                          value={actualBalances.sources?.buffer || ''}
                          onChange={(e) => setActualBalances(prev => ({
                            ...prev,
                            sources: { ...(prev.sources || {}), buffer: e.target.value }
                          }))}
                          className="w-full px-3 py-1.5 text-xs border border-cyan-200 dark:border-cyan-800 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                          placeholder="e.g. Cash Reserve ****9876"
                          data-testid="balance-source-buffer"
                          maxLength={80}
                        />
                      </div>
                      {balanceComparison?.comparison?.buffer && (
                        <div className="mt-2 text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Expected: </span>
                          <span className={balanceComparison.comparison.buffer.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                            ${balanceComparison.comparison.buffer.expected.toLocaleString()}
                          </span>
                          {balanceComparison.comparison.buffer.difference !== 0 && (
                            <span className={`ml-2 ${balanceComparison.comparison.buffer.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              ${Math.abs(balanceComparison.comparison.buffer.difference).toLocaleString()} {balanceComparison.comparison.buffer.difference > 0 ? 'above' : 'below'} expected
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Custom Allocations */}
                  {balanceComparison?.comparison?.custom_allocations && Object.keys(balanceComparison.comparison.custom_allocations).length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">Custom Allocations</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(balanceComparison.comparison.custom_allocations).map(([name, data]) => (
                          <div key={name} className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                            <label className="block text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                              {name}
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={actualBalances.custom_allocations?.[name] || ''}
                                onChange={(e) => setActualBalances(prev => ({
                                  ...prev,
                                  custom_allocations: {
                                    ...prev.custom_allocations,
                                    [name]: parseFloat(e.target.value) || 0
                                  }
                                }))}
                                className="w-full pl-8 pr-4 py-2 border border-orange-300 dark:border-orange-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                placeholder="0.00"
                                data-testid={`balance-custom-${name}`}
                              />
                            </div>
                            <div className="mt-2">
                              <label className="block text-[11px] font-medium text-orange-700 dark:text-orange-300 mb-1">
                                Source account (optional)
                              </label>
                              <input
                                type="text"
                                value={actualBalances.custom_sources?.[name] || ''}
                                onChange={(e) => setActualBalances(prev => ({
                                  ...prev,
                                  custom_sources: { ...(prev.custom_sources || {}), [name]: e.target.value }
                                }))}
                                className="w-full px-3 py-1.5 text-xs border border-orange-200 dark:border-orange-800 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                                placeholder="e.g. Vanguard IRA ****2222"
                                data-testid={`balance-source-custom-${name}`}
                                maxLength={80}
                              />
                            </div>
                            <div className="mt-2 text-xs">
                              <span className="text-gray-600 dark:text-gray-400">Expected: </span>
                              <span className={data.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                                ${data.expected.toLocaleString()}
                              </span>
                              {data.difference !== 0 && (
                                <span className={`ml-2 ${data.difference < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                  ${Math.abs(data.difference).toLocaleString()} {data.difference > 0 ? 'above' : 'below'} target allocation
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {balanceComparison?.last_updated 
                        ? `Last updated: ${new Date(balanceComparison.last_updated).toLocaleString()}`
                        : 'Not yet saved'}
                    </p>
                    <button
                      onClick={saveActualBalances}
                      disabled={balancesSaving}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center"
                      data-testid="save-balances-btn"
                    >
                      <Save className={`w-4 h-4 mr-2 ${balancesSaving ? 'animate-spin' : ''}`} />
                      {balancesSaving ? 'Saving...' : 'Save Balances'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* API Access Tab */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Access</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Generate an API key to connect Paper Trail with external applications like Command Center.
              </p>

              {/* API Key Management */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Your API Key</h4>
                
                {apiKeyStatus?.has_active_key ? (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        <Check className="w-4 h-4 mr-1" />
                        Active
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Created: {apiKeyStatus.created_at ? new Date(apiKeyStatus.created_at).toLocaleDateString() : 'Unknown'}
                      </span>
                      {apiKeyStatus.last_used && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          • Last used: {new Date(apiKeyStatus.last_used).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    
                    {newApiKey && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2 font-medium">
                          ⚠️ Save this key now! It won't be shown again.
                        </p>
                        <div className="flex items-center space-x-2">
                          <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 text-sm font-mono text-gray-900 dark:text-white overflow-x-auto">
                            {newApiKey}
                          </code>
                          <button
                            onClick={copyApiKey}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center"
                          >
                            {apiKeyCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex space-x-3">
                      <button
                        onClick={generateApiKey}
                        disabled={apiKeyLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${apiKeyLoading ? 'animate-spin' : ''}`} />
                        Regenerate Key
                      </button>
                      <button
                        onClick={revokeApiKey}
                        disabled={apiKeyLoading}
                        className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
                      >
                        Revoke Key
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-gray-600 dark:text-gray-400">
                      You don't have an active API key. Generate one to enable external integrations.
                    </p>
                    <button
                      onClick={generateApiKey}
                      disabled={apiKeyLoading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center"
                    >
                      <Key className={`w-4 h-4 mr-2 ${apiKeyLoading ? 'animate-spin' : ''}`} />
                      {apiKeyLoading ? 'Generating...' : 'Generate API Key'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;